import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Select avec recherche intégrée — composant partagé.
 * Utilise les classes .page-select / .page-select-label / .page-select-arrow de shared.css.
 *
 * Le dropdown est rendu dans un portail (document.body) avec position: fixed,
 * pour échapper aux overflow des ancêtres (cas typique : usage dans une cellule
 * de tableau avec `.table-container { overflow: auto }` qui sinon cliperait
 * le dropdown et empêcherait de scroller à l'intérieur).
 *
 * Props :
 *   value       — valeur sélectionnée
 *   options     — [{ value, label }, ...]
 *   onChange    — callback(value)
 *   placeholder — texte affiché quand rien n'est sélectionné
 *   highlighted — booléen, ajoute .active-filter si true
 */
export default function SearchableSelect({ value, options, onChange, placeholder, highlighted }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef(null);
  const popoverRef = useRef(null);

  // Click extérieur → ferme. Le popover étant en portail (hors de ref),
  // on doit aussi vérifier qu'on n'a pas cliqué dedans.
  useEffect(() => {
    const handler = (e) => {
      const inButton = ref.current && ref.current.contains(e.target);
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inButton && !inPopover) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Recalcule la position du popover quand on ouvre, et ferme si l'utilisateur
  // scrolle hors du popover ou redimensionne la fenêtre (le popover est en
  // position: fixed et ne suit pas le bouton).
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    // Phase capture pour intercepter le scroll dans n'importe quel ancêtre,
    // mais on ignore le scroll interne du popover (sinon il se fermerait dès
    // qu'on fait défiler la liste pour voir les options du bas).
    const onScroll = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );
  const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

  const popover = open ? createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.width, zIndex: 10000,
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 260, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <input
        autoFocus
        type="text"
        placeholder="Rechercher…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border-default)', outline: 'none', fontSize: '0.9rem', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
      />
      <div style={{ overflowY: 'auto', maxHeight: 210 }}>
        {filtered.map(o => (
          <div
            key={o.value}
            onClick={() => { onChange(o.value); setOpen(false); }}
            style={{
              padding: '7px 12px', cursor: 'pointer', fontSize: '0.9rem',
              background: o.value === value ? 'var(--bg-hover)' : 'transparent',
              color: 'var(--text-primary)',
              fontWeight: o.value === value ? 600 : 400,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = o.value === value ? 'var(--bg-hover)' : 'transparent'; }}
          >
            {o.label}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: '7px 12px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Aucun résultat</div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={`page-select${highlighted ? ' active-filter' : ''}`}
        onClick={() => { setOpen(v => !v); setSearch(''); }}
      >
        <span className="page-select-label">{selectedLabel}</span>
        <span className="page-select-arrow">▾</span>
      </button>
      {popover}
    </div>
  );
}
